import { EditableTitle } from '@/components/Title/EditableTitle';
import { toast } from '@/components/toast';
import { PolotnoStoreKeys, useCurrentProjectId, usePolotnoState } from '@/pages/AdvancedEditor/hooks';
import { updateVideo } from '@/services/api';
import { useAuth } from '@/services/auth';
import { logger } from '@/services/logger';
import { DEFAULT_VIDEO_NAME } from '@/state/presenters/presenters';
import { LibraryItemType } from '@d-id/types';
import styled from '@emotion/styled';
import mixpanel from 'mixpanel-browser';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';
import useGetProject from '../../hooks/useGetProject';

const VideoTitleInputWrapper = styled.div`
    top: 20px;
    left: 30px;

    color: ${({ theme }) => theme.colors.secondary[500]};
    cursor: pointer;
`;

const StyledEditableTitle = styled(EditableTitle)<{ editorTopBarEnabled: boolean }>`
    border: none !important;
    ${({ editorTopBarEnabled }) => editorTopBarEnabled && 'max-width: 240px'};
    font-size: 18px;
    font-weight: 500;
    line-height: 160%;
    letter-spacing: 0.15px;
    color: ${({ theme }) => theme.colors.black[700]};

    > input,
    > div {
        font-size: 14px;
    }
`;

export const VideoTitleInput = observer(() => {
    const projectId = useCurrentProjectId();
    const { data: project } = useGetProject(projectId);
    const { isAnonymous } = useAuth();
    const [videoName, setStoreVideoName] = usePolotnoState(PolotnoStoreKeys.creativeVideoName);
    const [localVideoName, setLocalVideoName] = useState<string>(videoName);

    useEffect(() => {
        setLocalVideoName(videoName);
    }, [videoName]);

    const queryClient = useQueryClient();

    const updateName = async (name: string) => {
        try {
            if (!isAnonymous) {
                await updateVideo(projectId, { name: name.trim() || DEFAULT_VIDEO_NAME }, LibraryItemType.PROJECT);
                await queryClient.invalidateQueries({ queryKey: [`get-project-${projectId}`] });
            }
        } catch (e) {
            toast.error({ title: 'Failed to update video name, please try again' });
            logger.error('Failed to update video name', { e });
        }
    };

    const handleBlur = async (value: string) => {
        if (value !== project?.name) {
            mixpanel?.track('project-name', { event: 'click', value, from: 'editor' });
            setStoreVideoName(value);
            await updateName(value);
        }
    };

    return (
        <VideoTitleInputWrapper>
            <StyledEditableTitle
                testId="editor-header-video-title-input"
                fontSize={18}
                value={localVideoName}
                onChange={setLocalVideoName}
                placeholder="Type your video title here"
                color="#5C5C5C"
                onBlur={handleBlur}
                showIcon
                showTooltip
                editorTopBarEnabled
            />
        </VideoTitleInputWrapper>
    );
});
